import { useMemo, useCallback, useEffect } from 'react';
import moment from 'moment';

import { useDateRangePicker } from './useDatePicker';
import usePersistentPeriod from './usePersistentPeriod';

const usePersistentDateRangePicker = (opts = {}) => {
  const { exclude = [], queryParam = 'dt' } = opts;

  const [period, setPeriod, getStoredDate, setStoredDateValue] = usePersistentPeriod({ exclude });
  const dateRangePicker = useDateRangePicker(period, { queryParam });

  const effectiveDateRange = useMemo(() => {
    const storedDate = getStoredDate();
    if (storedDate && moment.isMoment(storedDate)) {
      const yearAgo = moment().subtract(2, 'years');
      const yearFromNow = moment().add(1, 'year');
      
      if (storedDate.isBetween(yearAgo, yearFromNow)) {
        const unit = dateRangePicker.mode === 'date' ? 'day' : dateRangePicker.mode;
        const start = storedDate.clone().startOf(unit);
        const end = storedDate.clone().endOf(unit);
        return [start, end];
      }
    }
    return dateRangePicker.dateRange;
  }, [dateRangePicker.dateRange, dateRangePicker.mode, getStoredDate]);

  useEffect(() => {
    const storedDate = getStoredDate();
    if (storedDate && moment.isMoment(storedDate)) {
      const yearAgo = moment().subtract(2, 'years');
      const yearFromNow = moment().add(1, 'year');
      
      if (storedDate.isBetween(yearAgo, yearFromNow)) {
        const unit = dateRangePicker.mode === 'date' ? 'day' : dateRangePicker.mode;
        const start = storedDate.clone().startOf(unit);
        const end = storedDate.clone().endOf(unit);
        
        const currentStart = dateRangePicker.dateRange[0];
        if (!currentStart || !currentStart.isSame(start, unit)) {
          dateRangePicker.setDateRange([start, end]);
        }
      }
    }
  }, [dateRangePicker, getStoredDate]);

  const setDateRange = useCallback((newDateRange) => {
    dateRangePicker.setDateRange(newDateRange);
    if (newDateRange && newDateRange[0]) {
      setStoredDateValue(newDateRange[0]);
    }
  }, [dateRangePicker, setStoredDateValue]);

  const setPeriodAndClearDate = useCallback((newPeriod) => {
    if (newPeriod !== period) {
      setStoredDateValue(null);
    }
    setPeriod(newPeriod);
  }, [setPeriod, setStoredDateValue, period]);

  return {
    mode: dateRangePicker.mode,
    dateRange: effectiveDateRange,
    setDateRange,
    period,
    setPeriod: setPeriodAndClearDate,
  };
};

export default usePersistentDateRangePicker;
